/**
 * CartSummary — Collapsible cart with items, pricing breakdown
 * Optimized: memoized, cleaner price formatting, null-safe
 */

import React, { useMemo } from "react";
import {
  Collapse,
  List,
  Typography,
  Divider,
  Row,
  Col,
  Badge,
  Empty,
} from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";

const { Text } = Typography;

const formatCurrency = (amount, currency = "INR") =>
  `${currency} ${parseFloat(amount || 0).toFixed(2)}`;

const CartSummary = ({ cartData }) => {
  const totalItems = useMemo(
    () => cartData?.products?.reduce((sum, p) => sum + (p.quantity || 0), 0) || 0,
    [cartData]
  );

  if (!cartData || totalItems === 0) return null;

  const { total_amount, products = [] } = cartData;
  const currency = total_amount?.currency || "INR";
  const amount = total_amount?.amount || 0;

  return (
    <Collapse
      defaultActiveKey={[]}
      expandIconPosition="end"
      style={{ borderRadius: 10, border: "1px solid #f0f0f0", backgroundColor: "#fff" }}
      bordered={false}
      items={[
        {
          key: "1",
          label: (
            <Row justify="space-between" align="middle" style={{ width: "100%" }}>
              <Col>
                <ShoppingCartOutlined style={{ marginRight: 8 }} />
                <Text strong>{totalItems} {totalItems === 1 ? "Item" : "Items"}</Text>
              </Col>
              <Col>
                <Text strong style={{ fontSize: 16 }}>
                  {formatCurrency(amount, currency)}
                </Text>
              </Col>
            </Row>
          ),
          children: (
            <>
              <List
                itemLayout="horizontal"
                dataSource={products}
                locale={{ emptyText: <Empty description="No items" /> }}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Badge
                          count={item.quantity}
                          style={{ backgroundColor: "#666", borderRadius: "50%" }}
                        >
                          <img
                            src={item.image}
                            alt={item.title}
                            style={{ width: 40, borderRadius: 4 }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </Badge>
                      }
                      title={<Text style={{ fontWeight: 500 }}>{item.title}</Text>}
                      description={
                        item.variant ? (
                          <Text type="secondary">Variant: {item.variant}</Text>
                        ) : null
                      }
                    />
                  </List.Item>
                )}
              />

              <Divider style={{ margin: "12px 0" }} />

              <PriceRow label="Subtotal" value={formatCurrency(amount, currency)} />
              <PriceRow label="Discount" value="-₹0" valueType="success" />
              <PriceRow label="Shipping" value="Free" />

              <Divider style={{ margin: "12px 0" }} />

              <Row justify="space-between">
                <Text strong>Total</Text>
                <Text strong style={{ fontSize: 16 }}>
                  {formatCurrency(amount, currency)}
                </Text>
              </Row>
            </>
          ),
        },
      ]}
    />
  );
};

const PriceRow = ({ label, value, valueType }) => (
  <Row justify="space-between" style={{ marginBottom: 6 }}>
    <Text type="secondary">{label}</Text>
    <Text type={valueType}>{value}</Text>
  </Row>
);

export default React.memo(CartSummary);